import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Video, Plus, Calendar, Clock, Users, Link2, Copy, Check,
  Loader2, Trash2, ExternalLink, ChevronLeft, ChevronRight, PhoneOff
, CalendarPlus } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import SeriesDialog from './SeriesDialog';
import { useTranslation } from '../i18n';

// ============================================
// HELPERS
// ============================================

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function formatTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function formatDuration(minutes) {
  if (!minutes) return '';
  if (minutes < 60) return `${minutes}min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

function groupMeetingsByDate(meetings) {
  const groups = {};
  const now = new Date();
  const today = now.toDateString();
  const tomorrow = new Date(now.getTime() + 86400000).toDateString();

  for (const m of meetings) {
    const dateKey = m.scheduled_at
      ? new Date(m.scheduled_at).toDateString()
      : m.actual_started_at
        ? new Date(m.actual_started_at).toDateString()
        : today;

    let label = dateKey;
    if (dateKey === today) label = 'Today';
    else if (dateKey === tomorrow) label = 'Tomorrow';

    if (!groups[label]) groups[label] = [];
    groups[label].push(m);
  }
  return groups;
}

const STATUS_COLORS = {
  scheduled: 'bg-blue-100 text-blue-800',
  active: 'bg-green-100 text-green-800',
  completed: 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300',
  cancelled: 'bg-red-100 text-red-800',
};

// ============================================
// MEETING CARD
// ============================================

function MeetingCard({ meeting, onCancel, onEnd, onCopy, onViewSeries }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const joinUrl = `${window.location.origin}/meet/${meeting.join_code}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(joinUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    onCopy?.();
  };

  const handleJoin = () => {
    navigate(`/meet/${meeting.join_code}`);
  };

  const isJoinable = meeting.status === 'scheduled' || meeting.status === 'active';
  const timeStr = meeting.scheduled_at
    ? `${formatDate(meeting.scheduled_at)} at ${formatTime(meeting.scheduled_at)}`
    : meeting.actual_started_at
      ? `Started ${formatTime(meeting.actual_started_at)}`
      : '';

  return (
    <div className="flex items-center gap-4 p-4 rounded-lg border bg-card hover:bg-muted/30 transition-colors">
      <div className="p-2.5 rounded-lg bg-primary/10 shrink-0">
        <Video className="h-5 w-5 text-primary" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-medium truncate">{meeting.title}</p>
          <Badge variant="secondary" className={`text-xs ${STATUS_COLORS[meeting.status] || ''}`}>
            {meeting.status}
          </Badge>
          {meeting.recurrence && meeting.recurrence !== 'none' && (
            <Badge variant="outline" className="text-xs gap-1">
              🔁 {meeting.recurrence === 'weekly' ? (t("meetings.weekly") || 'Weekly')
                 : meeting.recurrence === 'biweekly' ? (t("meetings.biweekly") || 'Every 2 weeks')
                 : (t("meetings.monthly") || 'Monthly')}
            </Badge>
          )}
          {meeting.series_id && (
            <button
              onClick={() => onViewSeries?.(meeting.series_id)}
              className="text-xs text-primary hover:underline"
            >
              View series
            </button>
          )}
        </div>
        <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground flex-wrap">
          {timeStr && (
            <span className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              {timeStr}
            </span>
          )}
          <span className="flex items-center gap-1">
            <Users className="h-3.5 w-3.5" />
            {(meeting.invitees?.length || 0) + (meeting.participants?.length || 0)} invited
          </span>
          {meeting.duration_minutes && (
            <span>{formatDuration(meeting.duration_minutes)}</span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {isJoinable && (
          <>
            <Button variant="ghost" size="icon" onClick={handleCopy} title="Copy join link">
              {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
            </Button>
            <Button size="sm" onClick={handleJoin} className="gap-1.5">
              <Video className="h-4 w-4" />
              <span className="hidden sm:inline">Join</span>
            </Button>
          </>
        )}
        {meeting.status === 'active' && (
          <Button
            variant="destructive"
            size="sm"
            onClick={() => onEnd(meeting._id)}
            title="End meeting for all"
            className="gap-1.5"
          >
            <PhoneOff className="h-4 w-4" />
            <span className="hidden sm:inline">End</span>
          </Button>
        )}
        {meeting.status === 'scheduled' && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onCancel(meeting._id)}
            title={t("meetings.cancelMeeting") || "Cancel meeting"}
            className="text-destructive hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

// ============================================
// SCHEDULE MEETING DIALOG
// ============================================

function ScheduleMeetingDialog({
  open, onOpenChange, onCreated }) {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [type, setType] = useState('scheduled');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [duration, setDuration] = useState('30');
  const [inviteeEmail, setInviteeEmail] = useState('');
  const [invitees, setInvitees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allowRecording, setAllowRecording] = useState(false);
  const [recurrence, setRecurrence] = useState('none');
  const [waitingRoom, setWaitingRoom] = useState(false);

  const addInvitee = () => {
    const email = inviteeEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) return;
    if (invitees.some(i => i.email === email)) return;
    setInvitees([...invitees, { email, name: '' }]);
    setInviteeEmail('');
  };

  const removeInvitee = (email) => {
    setInvitees(invitees.filter(i => i.email !== email));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addInvitee();
    }
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error('Please enter a meeting title');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        title: title.trim(),
        type,
        duration_minutes: parseInt(duration),
        invitees,
      };

      if (allowRecording) {
        payload.settings = { ...(payload.settings || {}), allow_recording: true };
      }

      if (waitingRoom) {
        payload.settings = { ...(payload.settings || {}), waiting_room: true };
      }

      if (type === 'scheduled') {
        if (!date || !time) {
          toast.error('Please select a date and time');
          setLoading(false);
          return;
        }
        payload.scheduled_at = new Date(`${date}T${time}`).toISOString();
      }

      if (recurrence !== 'none') {
        payload.recurrence = recurrence;
      }

      const result = await api.post('/meetings', payload);
      toast.success(type === 'instant' ? 'Meeting started!' : 'Meeting scheduled!');
      onCreated?.(result);
      onOpenChange(false);

      // Reset form
      setTitle('');
      setDate('');
      setTime('');
      setDuration('30');
      setInvitees([]);
      setAllowRecording(false);
      setRecurrence('none');
      setWaitingRoom(false);
      setType('scheduled');
    } catch (err) {
      toast.error(err.message || 'Failed to create meeting');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Video className="h-5 w-5 text-primary" />
            {type === 'instant' ? t('meetings.startInstantMeeting') || 'Start Instant Meeting' : t('meetings.scheduleMeeting') || 'Schedule Meeting'}
          </DialogTitle>
          <DialogDescription>
            {type === 'instant'
              ? 'Start a video meeting right now and invite others.'
              : t('meetings.scheduleDesc') || 'Schedule a video meeting and send invites.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Meeting type toggle */}
          <div className="flex gap-2">
            <Button
              variant={type === 'scheduled' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setType('scheduled')}
              className="flex-1"
            >
              <Calendar className="h-4 w-4 mr-1.5" />
              {t("meetings.schedule") || "Schedule"}
            </Button>
            <Button
              variant={type === 'instant' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setType('instant')}
              className="flex-1"
            >
              <Video className="h-4 w-4 mr-1.5" />
              {t("meetings.startNow") || "Start Now"}
            </Button>
          </div>

          {/* Title */}
          <div className="space-y-1.5">
            <Label htmlFor="meetTitle">{t("meetings.meetingTitle") || "Meeting Title"}</Label>
            <Input
              id="meetTitle"
              placeholder="Team sync, Client walkthrough..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          {/* Date & Time (scheduled only) */}
          {type === 'scheduled' && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="meetDate">{t("meetings.date") || "Date"}</Label>
                <Input
                  id="meetDate"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="meetTime">{t("meetings.time") || "Time"}</Label>
                <Input
                  id="meetTime"
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Duration */}
          <div className="space-y-1.5">
            <Label>{t("meetings.duration") || "Duration"}</Label>
            <Select value={duration} onValueChange={setDuration}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="15">{t("meetings.15min") || "15 minutes"}</SelectItem>
                <SelectItem value="30">{t("meetings.30min") || "30 minutes"}</SelectItem>
                <SelectItem value="45">{t("meetings.45min") || "45 minutes"}</SelectItem>
                <SelectItem value="60">1 hour</SelectItem>
                <SelectItem value="90">1.5 hours</SelectItem>
                <SelectItem value="120">2 hours</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Recurrence (scheduled only) */}
          {type === 'scheduled' && (
            <div className="space-y-1.5">
              <Label>{t("meetings.repeat") || "Repeat"}</Label>
              <Select value={recurrence} onValueChange={setRecurrence}>
                <SelectTrigger>
                  <SelectValue placeholder={t("meetings.doesNotRepeat") || "Does not repeat"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("meetings.doesNotRepeat") || "Does not repeat"}</SelectItem>
                  <SelectItem value="weekly">{t("meetings.weekly") || "Weekly"}</SelectItem>
                  <SelectItem value="biweekly">{t("meetings.biweekly") || "Every 2 weeks"}</SelectItem>
                  <SelectItem value="monthly">{t("meetings.monthly") || "Monthly"}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Invitees */}
          <div className="space-y-1.5">
            <Label>{t("meetings.invitePeople") || "Invite People (optional)"}</Label>
            <div className="flex gap-2">
              <Input
                placeholder={t("meetings.emailPlaceholder") || "email@example.com"}
                value={inviteeEmail}
                onChange={(e) => setInviteeEmail(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <Button variant="outline" size="sm" onClick={addInvitee} className="shrink-0">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {invitees.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {invitees.map(inv => (
                  <Badge
                    key={inv.email}
                    variant="secondary"
                    className="gap-1 cursor-pointer hover:bg-destructive/10"
                    onClick={() => removeInvitee(inv.email)}
                  >
                    {inv.email}
                    <span className="text-destructive">×</span>
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Allow Recording Toggle */}
        <div className="flex items-center justify-between gap-3 rounded-lg border p-3">
          <div className="space-y-0.5">
            <Label htmlFor="allowRecording" className="text-sm font-medium cursor-pointer">
              {t("meetings.allowRecording") || "Allow recording"}
            </Label>
            <p className="text-xs text-muted-foreground">
              {t("meetings.recordingDesc") || "Enables the host to record this meeting"}
            </p>
          </div>
          <Switch
            id="allowRecording"
            checked={allowRecording}
            onCheckedChange={setAllowRecording}
          />
        </div>

        {/* Waiting Room Toggle */}
        <div className="flex items-center justify-between gap-3 rounded-lg border p-3">
          <div className="space-y-0.5">
            <Label htmlFor="waitingRoom" className="text-sm font-medium cursor-pointer">
              {t("meetings.waitingRoom") || "Waiting Room"}
            </Label>
            <p className="text-xs text-muted-foreground">
              {t("meetings.waitingRoomDesc") || "Participants must be admitted by the host before joining"}
            </p>
          </div>
          <Switch
            id="waitingRoom"
            checked={waitingRoom}
            onCheckedChange={setWaitingRoom}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("common.cancel") || "Cancel"}</Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating...</>
            ) : type === 'instant' ? (
              <><Video className="h-4 w-4 mr-2" /> Start Meeting</>
            ) : (
              <><Calendar className="h-4 w-4 mr-2" /> {t("meetings.scheduleMeeting") || "Schedule Meeting"}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================
// MAIN PAGE
// ============================================


// Download .ics file for a meeting
const downloadIcs = async (meetingId) => {
  try {
    const token = localStorage.getItem('token');
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/meetings/${meetingId}/ics`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'meeting.ics';
    a.click();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error('Failed to download calendar file');
  }
};

export default function MeetingsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('upcoming'); // upcoming, past, calendar
  const [showSchedule, setShowSchedule] = useState(false);
  const [seriesId, setSeriesId] = useState(null);
  const [showSeriesDialog, setShowSeriesDialog] = useState(false);

  const handleViewSeries = (sid) => {
    setSeriesId(sid);
    setShowSeriesDialog(true);
  };

  const fetchMeetings = useCallback(async () => {
    try {
      const data = await api.get('/meetings');
      setMeetings(data.meetings || []);
    } catch (err) {
      console.error('Failed to fetch meetings:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMeetings();
  }, [fetchMeetings]);

  const handleCancel = async (meetingId) => {
    if (!confirm(t('meetings.cancelConfirm') || 'Cancel this meeting?')) return;
    try {
      await api.delete(`/meetings/${meetingId}`);
      toast.success('Meeting cancelled');
      fetchMeetings();
    } catch (err) {
      toast.error('Failed to cancel meeting');
    }
  };

  const handleEnd = async (meetingId) => {
    if (!confirm('End this meeting for all participants?')) return;
    try {
      await api.post(`/meetings/${meetingId}/end`);
      toast.success('Meeting ended');
      fetchMeetings();
    } catch (err) {
      toast.error('Failed to end meeting');
    }
  };

  const handleCreated = (meeting) => {
    fetchMeetings();
    if (meeting.type === 'instant') {
      navigate(`/meet/${meeting.join_code}`);
    }
  };

  const now = new Date();
  const upcomingMeetings = meetings.filter(m =>
    m.status === 'scheduled' || m.status === 'active'
  );
  const pastMeetings = meetings.filter(m =>
    m.status === 'completed' || m.status === 'cancelled'
  );

  const displayMeetings = view === 'past' ? pastMeetings : upcomingMeetings;
  const grouped = groupMeetingsByDate(displayMeetings);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Meetings</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {t("meetings.subtitle") || "Schedule and manage video meetings"}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setShowSchedule(true);
            }}
          >
            <Calendar className="h-4 w-4 mr-2" />
            Schedule
          </Button>
          <Button
            onClick={() => {
              setShowSchedule(true);
            }}
          >
            <Video className="h-4 w-4 mr-2" />
            {t("meetings.startInstant") || "Start Instant"}
          </Button>
        </div>
      </div>

      {/* View Toggle */}
      <div className="flex gap-1 p-1 bg-muted rounded-lg w-fit">
        <button
          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
            view === 'upcoming' ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => setView('upcoming')}
        >
          {t("meetings.upcoming") || "Upcoming"} ({upcomingMeetings.length})
        </button>
        <button
          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
            view === 'past' ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => setView('past')}
        >
          {t("meetings.past") || "Past"} ({pastMeetings.length})
        </button>
      </div>

      {/* Meeting List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : displayMeetings.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Video className="h-12 w-12 text-muted-foreground/30 mb-4" />
            <p className="text-muted-foreground font-medium">
              {view === 'past' ? t('meetings.noPast') || 'No past meetings' : t('meetings.noUpcoming') || 'No upcoming meetings'}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              {view === 'past'
                ? 'Your completed meetings will appear here.'
                : 'Schedule a meeting or start one now.'}
            </p>
            {view === 'upcoming' && (
              <Button className="mt-4" onClick={() => setShowSchedule(true)}>
                <Plus className="h-4 w-4 mr-2" />
                {t("meetings.createMeeting") || "Create Meeting"}
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([dateLabel, dateMeetings]) => (
            <div key={dateLabel}>
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                {dateLabel}
              </h3>
              <div className="space-y-2">
                {dateMeetings.map(meeting => (
                  <MeetingCard
                    key={meeting._id}
                    meeting={meeting}
                    onCancel={handleCancel}
                    onEnd={handleEnd}
                    onCopy={() => toast.success('Join link copied!')}
                    onViewSeries={handleViewSeries}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Schedule Dialog */}
      <ScheduleMeetingDialog
        open={showSchedule}
        onOpenChange={setShowSchedule}
        onCreated={handleCreated}
      />

      <SeriesDialog
        open={showSeriesDialog}
        onOpenChange={setShowSeriesDialog}
        seriesId={seriesId}
        onSeriesCancelled={fetchMeetings}
      />
    </div>
  );
}
